<template>
  <b-container fluid class="pt-3 px-5 flex-grow-1 d-flex flex-column" style="background-color: #f2f5f8">
    <b-row class="h-100">
      <b-col cols="12" lg="8" class="px-2 mb-3">
        <r-card
            title="Priorities"
            class="h-100"
        >
          <template #title>
            <div class="d-flex">
              <h4 class="card-title">Work plans</h4>
            </div>
          </template>
          <vue-element-loading :active="loading"/>
          <ag-grid-vue
              class="ag-theme-balham"

              :cacheQuickFilter="cacheQuickFilter"

              :columnDefs="columnDefs"
              @columnMoved="saveGridOptions"
              @columnPinned="saveGridOptions"
              @columnResized="saveGridOptions"
              :columnTypes="columnTypes"
              @columnVisible="saveGridOptions"
              :defaultColDef="defaultColDef"

              @filterChanged="saveGridOptions"
              :floatingFilter="floatingFilter"
              :gridOptions="gridOptions"
              @grid-ready="onGridReady"

              :headerHeight="rowHeight"
              :quickFilter="query.search"
              :rowData="rowData"

              :rowHeight="rowHeight"
              @rowSelected="updateRowSelected"

              :rowSelection="rowSelection"
              :side-bar="sideBarDef"

              @sortChanged="saveGridOptions"

              :style="'width: ' + gridWidth +'%; height: 90%;'"
              :suppressExcelExport="suppressExcelExport"
          ></ag-grid-vue>
        </r-card>
      </b-col>
      <b-col cols="12" lg="4" class="px-2 mb-3">
        <r-card
            :title="planToEdit ? 'Edit work plan' : 'Create new work plan'"
        >
          <b-row>
            <b-col cols="12">
              <b-form-group
                  label="Plan name"
                  label-size="sm"
              >
                <b-form-input
                    placeholder="Plan name"
                    v-model="newPlanName"
                    ref="newPlanName"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                  label="Plan due date"
                  label-size="sm"
              >
                <date-picker
                    ref="datePicker"
                    id="internalDueDate"
                    v-model="newPlanDueDate"
                    :confirm-buttons="false"
                    :reduceButtons="false"
                    :calendar-icon="true"
                    :placeholder="'Plan due date'"
                >
                </date-picker>
              </b-form-group>
              <div class="flex justify-content-between">
                <b-button
                    variant="outline-primary"
                    class="text-center text-nowrap py-1 px-2 filters-toggle"
                    style="width: 100px"
                    @click="clearNewPlan"
                >
                  CANCEL
                </b-button>
                <b-button
                    v-if="!planToEdit"
                    variant="primary"
                    class="text-center text-nowrap py-1 px-2 filters-toggle"
                    style="width: 220px"
                    @click="save"
                >
                  CREATE NEW WORK PLAN
                </b-button>
                <b-button
                    v-if="planToEdit"
                    variant="primary"
                    class="text-center text-nowrap py-1 px-2 filters-toggle"
                    style="width: 220px"
                    @click="saveEdit"
                >
                  SAVE CHANGES
                </b-button>
              </div>
              <vue-element-loading :active="saving"/>
            </b-col>
          </b-row>
        </r-card>
      </b-col>

    </b-row>
  </b-container>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import RCard from '@/components/Layout/RCard';
import GridMixin from '@/components/Grid/GridMixin';
import {
  workPlansGridColumnTypes,
  workPlansGridDefaultColDef,
  queryGridColumns,
  getWorkPlansGridColumns,
  workPlansGridColumns,
} from '@/services/WorkPlans/WorkPlansGridConfig';
import DatePicker from '@/components/Form/DatePicker';
import WorkPlans from '@/services/WorkPlans/WorkPlans';
import { mapGetters } from 'vuex';
import ErrorMixin from '@/components/Notifications/ErrorMixin';

export default {
  name: 'WorkPlans',
  components: {
    AgGridVue,
    RCard,
    DatePicker,
  },
  mixins: [GridMixin, ErrorMixin],
  data() {
    return {
      columnDefs: workPlansGridColumns,
      columnTypes: workPlansGridColumnTypes,
      defaultColDef: workPlansGridDefaultColDef,
      newPlanDueDate: null,
      newPlanName: null,
      newPlanNameValid: null,
      newDueDateValid: null,
      saving: false,
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      clientId: 'userClientId',
      planToEdit: 'workPlanToEdit',
    }),
  },
  watch: {
    planToEdit(newValue, oldValue) {
      if (newValue) {
        this.newPlanName = newValue.name;
        this.newPlanDueDate = newValue.dueDate;
      } else {
        this.newPlanName = '';
        this.newPlanDueDate = '';
      }
    },
  },
  mounted() {
    this.fetchWorkPlans();
  },
  methods: {
    async fetchWorkPlans() {
      this.loading = true;
      await WorkPlans.list(this.clientId, true)
        .then((res) => {
          this.rowData = res.data.data;
        })
        .catch((e) => {
          this.showErrorMessage(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearNewPlan() {
      this.newPlanName = null;
      this.newPlanDueDate = null;
      if (this.planToEdit) {
        this.$notify({
          title: 'Success',
          text: 'Work plan changes cancelled',
          type: 'success',
        });
      }
      this.$store.commit('setPlanToEdit', null);
    },
    save() {
      if (!this.newPlanName || !this.newPlanDueDate) {
        this.notifyError('Set a plan name and due date');
        return;
      }
      this.saving = true;
      WorkPlans.create(this.clientId, this.newPlanName, new Date(this.newPlanDueDate).toDateString())
        .then((res) => {
          this.$notify({
            title: 'Success',
            text: 'New work plan successfully created',
            type: 'success',
          });
          this.fetchWorkPlans();
          this.clearNewPlan();
        })
        .catch((e) => {
          this.showErrorMessage(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    saveEdit() {
      if (!this.newPlanName || !this.newPlanDueDate) {
        this.notifyError('Set a plan name and due date');
        return;
      }
      this.saving = true;
      WorkPlans.update(this.planToEdit.workPlanId, this.newPlanName, new Date(this.newPlanDueDate).toDateString())
        .then((res) => {
          this.$notify({
            title: 'Success',
            text: 'Work plan changes saved',
            type: 'success',
          });
          this.fetchWorkPlans();
          this.$store.commit('setPlanToEdit', null);
          this.clearNewPlan();
        })
        .catch((e) => {
          this.showErrorMessage(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    editPlan(plan) {
      this.newPlanName = plan.name;
      this.newPlanDueDate = plan.dueDate;
    },
  },

};
</script>

<style scoped>

</style>
